import { useNavigate } from 'react-router'
import { REVIEW_SESSION_LIMIT } from '@shared/srs'
import { VivaWordmark } from '@web/components/brand/Logo'
import { useAuth } from '@web/lib/auth'
import { greeting, useDashboard } from '@web/lib/progress'
import {
  CountdownCard,
  MissionCard,
  StatsGrid,
  StreakCard,
  WeakAreasCard,
} from './cards'

/**
 * Home.
 *
 * Ordered by what she needs in the first three seconds: why she is doing this
 * (the countdown), what to do right now (the mission), then how it is going.
 * One request feeds the whole screen.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isPending, error } = useDashboard()
  const hello = greeting()

  // A full round's worth is the line between "there is some review to do" and
  // "review is the thing to do".
  const reviewLeads = (data?.dashboard.stats.wordsDue ?? 0) >= REVIEW_SESSION_LIMIT

  return (
    <div className="mx-auto w-full max-w-lg px-5 pt-[max(1.25rem,var(--safe-top))] pb-6">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <VivaWordmark className="text-2xl" />
          <p className="mt-1.5 truncate text-ink-700">
            <span lang="pt-BR" className="font-medium">
              {hello.pt}
            </span>
            , {user?.displayName}
          </p>
        </div>
      </header>

      {isPending && <Skeleton />}

      {error && !data && (
        <p className="rounded-card bg-cream p-5 text-ink-700">
          Could not load your progress. It will be here when you are back online.
        </p>
      )}

      {data && (
        <div className="space-y-4">
          <CountdownCard trip={data.dashboard.trip} />

          {/*
            When a full round is waiting, review comes before the next lesson.
            The old order told her to start new material every single day, and
            she followed it — sixteen lessons in, sixty of her words had never
            come back once. New words she cannot recall are not progress.
          */}
          {reviewLeads && (
            <ReviewCard
              due={data.dashboard.stats.wordsDue}
              lead
              onStart={() => void navigate('/review')}
            />
          )}
          <MissionCard
            mission={data.dashboard.mission}
            onStart={(lessonId) => void navigate(`/lesson/${lessonId}`)}
          />
          {!reviewLeads && (
            <ReviewCard
              due={data.dashboard.stats.wordsDue}
              onStart={() => void navigate('/review')}
            />
          )}
          <StreakCard
            streak={data.dashboard.streak}
            goal={data.dashboard.goal}
            week={data.dashboard.week}
          />
          <StatsGrid stats={data.dashboard.stats} />
          <WeakAreasCard areas={data.dashboard.weakAreas} />
        </div>
      )}
    </div>
  )
}

/**
 * Prompts a review only when something is actually due.
 *
 * Hidden rather than shown-as-empty on purpose: a permanent "0 words to
 * review" tile trains her to ignore the row, and then she misses the day it
 * matters.
 */
function ReviewCard({
  due,
  lead = false,
  onStart,
}: {
  due: number
  /** Leading the page: bigger, warmer, and phrased as the task of the day. */
  lead?: boolean
  onStart: () => void
}) {
  if (due <= 0) return null

  return (
    <button
      type="button"
      onClick={onStart}
      className={
        lead
          ? 'flex w-full items-center gap-4 rounded-card bg-gold-100 p-6 text-left ring-1 ring-gold-300 transition-colors hover:bg-gold-200'
          : 'flex w-full items-center gap-4 rounded-card border-l-4 border-gold-500 bg-cream p-5 text-left ring-1 ring-brand-900/5 transition-colors hover:bg-gold-100'
      }
    >
      <span aria-hidden="true" className={lead ? 'text-4xl' : 'text-3xl'}>
        🔁
      </span>
      <span className="flex-1">
        {lead && (
          <span className="mb-1 block text-xs font-semibold tracking-[0.16em] text-gold-700 uppercase">
            Start here today
          </span>
        )}
        <span
          className={`block font-display font-bold text-ink-900 ${lead ? 'text-xl' : 'text-lg'}`}
        >
          {due} {due === 1 ? 'word' : 'words'} to review
        </span>
        <span className="block text-sm text-ink-700">
          {lead
            ? 'These are words you have already met. Bring them back before you meet new ones.'
            : 'A few minutes now keeps them from slipping away.'}
        </span>
      </span>
      <span aria-hidden="true" className="text-ink-400">
        →
      </span>
    </button>
  )
}

/** Shaped like the real cards, so the page does not jump when data lands. */
function Skeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="h-56 animate-pulse rounded-card bg-sand-100" />
      <div className="h-44 animate-pulse rounded-card bg-sand-100" />
      <div className="h-36 animate-pulse rounded-card bg-sand-100" />
    </div>
  )
}
